import { useState, useRef, useEffect } from "react";
import TwitterCarousel from "../TwitterCarousel";
import x from "../../../assets/socialglass/x.svg";

function TwitterComponent({
  downloaded_path,
  muted,
  url,
  videoRef,
  onManualUnmute,
  onManualMute,
  showIcon = false,
  isAutoPlay = true,
  rounded = false,
}) {
  const isCarousel =
    Array.isArray(downloaded_path) && downloaded_path.length > 1;
  let mediaSources;
  try {
    mediaSources = Array.isArray(downloaded_path)
      ? downloaded_path
      : JSON.parse(downloaded_path);
  } catch (error) {
    console.error("Error parsing downloaded_path:", error);
    mediaSources = [downloaded_path];
  }

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(muted);
  const [isImageInView, setIsImageInView] = useState(false);
  const localRef = useRef(null);
  const imageWrapperRef = useRef(null);

  // Mute handling
  useEffect(() => {
    const videoEl = localRef.current;
    if (videoEl) {
      videoEl.muted = muted;
      setIsMuted(muted);
    }
  }, [muted]);

  // Play state tracking
  useEffect(() => {
    const videoEl = localRef.current;
    if (!videoEl) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    videoEl.addEventListener("play", handlePlay);
    videoEl.addEventListener("pause", handlePause);

    return () => {
      videoEl.removeEventListener("play", handlePlay);
      videoEl.removeEventListener("pause", handlePause);
    };
  }, []);

  // Intersection observer for images
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsImageInView(entry.isIntersecting),
      { threshold: 0.5 },
    );
    const imgEl = imageWrapperRef.current;
    if (imgEl) observer.observe(imgEl);
    return () => imgEl && observer.unobserve(imgEl);
  }, []);

  const handlePlayPause = () => {
    const videoEl = localRef.current;
    if (videoEl) {
      if (isPlaying) {
        videoEl.pause();
      } else {
        videoEl.play();
      }
    }
  };

  const handleMuteToggle = (e) => {
    e.stopPropagation(); // Prevent play/pause toggle
    const videoEl = localRef.current;
    if (videoEl) {
      videoEl.muted = !videoEl.muted;
      setIsMuted(videoEl.muted);
      if (!videoEl.muted) {
        onManualUnmute?.();
      } else {
        onManualMute?.();
      }
    }
  };

  if (!url) return <p>Invalid URL</p>;

  if (isCarousel) {
    return (
      <TwitterCarousel
        downloaded_path={downloaded_path}
        videoRef={videoRef}
        muted={muted}
        onManualUnmute={onManualUnmute}
        onManualMute={onManualMute}
      />
    );
  }
  return (
    <div
      ref={imageWrapperRef}
      className={`relative ${rounded && "rounded-lg"} flex aspect-[1/1.75] h-full w-full cursor-pointer items-center justify-center overflow-hidden bg-black`}
      onClick={handlePlayPause}
    >
      {showIcon && (
        <img
          src={x}
          alt="Twitter Logo"
          className="absolute left-2 top-2 h-6 w-6"
        />
      )}
      {mediaSources[0]?.split(".").pop().toLowerCase() === "mp4" ? (
        <>
          <video
            ref={(el) => {
              if (el) {
                videoRef(el);
                localRef.current = el;
              }
            }}
            src={`https://genz-staging.feeltiptop.com/${mediaSources[0]}`}
            muted={muted}
            {...(isAutoPlay ? { autoPlay: true } : {})}
            loop
            controlsList="nofullscreen"
            className="w-full object-cover"
            playsInline
            webkit-playsinline
          />
          <div
            className={`absolute inset-0 bg-black transition-opacity duration-500 ${
              isPlaying ? "opacity-0" : "opacity-60"
            }`}
          ></div>
          <button
            onClick={handleMuteToggle}
            className="absolute right-2 top-2 rounded-full bg-gray-700 p-2 text-white"
          >
            {isMuted ? "🔇" : "🔊"}
          </button>
        </>
      ) : (
        <>
          <img
            src={`https://genz-staging.feeltiptop.com/${mediaSources[0]}`}
            alt="Twitter content"
            className="w-full object-cover"
          />
          <div
            className={`absolute inset-0 bg-black transition-opacity duration-500 ${
              isImageInView ? "opacity-0" : "opacity-60"
            }`}
          ></div>
        </>
      )}
    </div>
  );
}

export default TwitterComponent;
